/**
 * SecurityProTemplate — multi-page site for a security guard company.
 * First customer: TopFlight Protection (owner ask 2026-11-03).
 *
 * Pages (path after the site base): /  /services  /industries  /about
 * /careers  /contact. Light + dark theme (follows the device until the
 * visitor picks one; the pick is remembered in this browser only).
 * Forms post to /api/website-leads (same endpoint as every customer site).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as Icons from 'lucide-react';

import './security-pro.css';

const API = process.env.REACT_APP_BACKEND_URL || '';
const THEME_KEY = 'sp-theme';

const PAGES = [
  { path: '', label: 'Home' },
  { path: 'services', label: 'Services' },
  { path: 'industries', label: 'Industries' },
  { path: 'about', label: 'About' },
  { path: 'careers', label: 'Careers' },
  { path: 'contact', label: 'Contact' },
];

function Icon({ name, size = 22, ...rest }) {
  const C = Icons[name] || Icons.Shield;
  return <C size={size} aria-hidden="true" {...rest} />;
}

function readStoredTheme() {
  try {
    const t = window.localStorage.getItem(THEME_KEY);
    return t === 'light' || t === 'dark' ? t : null;
  } catch {
    return null;
  }
}

function useTheme() {
  const systemDark = () =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false;
  const [picked, setPicked] = useState(() => (typeof window !== 'undefined' ? readStoredTheme() : null));
  const [sysDark, setSysDark] = useState(systemDark);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = (e) => setSysDark(e.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, []);

  const theme = picked || (sysDark ? 'dark' : 'light');
  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setPicked(next);
    try {
      window.localStorage.setItem(THEME_KEY, next);
    } catch {
      /* private mode: the choice just isn't remembered */
    }
  };
  return [theme, toggle];
}

/** Base path: '' on the custom domain, '/sites/<slug>' on the Agent9 preview URL. */
function useSiteBase(config) {
  const params = useParams();
  const { pathname } = useLocation();
  const base = params?.slug ? `/sites/${params.slug}` : '';
  let rest = pathname.startsWith(base) ? pathname.slice(base.length) : pathname;
  rest = rest.replace(/^\/+|\/+$/g, '').split('/')[0] || '';
  return { base, page: PAGES.some((p) => p.path === rest) ? rest : '__404__' };
}

const telHref = (p) => `tel:${String(p || '').replace(/[^+\d]/g, '')}`;

export default function SecurityProTemplate({ config }) {
  const [theme, toggleTheme] = useTheme();
  const { base, page } = useSiteBase(config);
  const [menuOpen, setMenuOpen] = useState(false);
  const href = (p) => `${base}/${p}`.replace(/\/+$/, '') || '/';

  useEffect(() => {
    setMenuOpen(false);
    if (typeof window !== 'undefined' && window.scrollTo) window.scrollTo(0, 0);
  }, [page]);

  const pageTitle = useMemo(() => {
    const p = PAGES.find((x) => x.path === page);
    if (!p || !p.path) return config.seo?.title || config.business_name;
    return `${p.label} | ${config.business_name}`;
  }, [page, config]);

  const ctx = { config, href, theme };

  return (
    <div className="sp-root" data-theme={theme} data-testid="ws-template-security-pro">
      <SiteHead config={config} title={pageTitle} page={page} theme={theme} />
      <UtilityBar config={config} />
      <header className="sp-nav" data-testid="sp-nav">
        <div className="sp-wrap">
          <Link to={href('')} className="sp-logo" aria-label={`${config.business_name} home`}>
            <img src={theme === 'dark' ? config.logo.dark : config.logo.light} alt={config.business_name} data-testid="sp-logo" />
          </Link>
          <nav className="sp-links" aria-label="Main">
            {PAGES.slice(1).map((p) => (
              <Link key={p.path} to={href(p.path)} className="sp-link" aria-current={page === p.path ? 'page' : undefined} data-testid={`sp-nav-${p.path}`}>
                {p.label}
              </Link>
            ))}
          </nav>
          <div className="sp-nav-right">
            <button type="button" className="sp-icon-btn" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'} data-testid="sp-theme-toggle">
              <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={18} />
            </button>
            <Link to={href('contact')} className="sp-btn sp-btn-primary sp-btn-sm sp-nav-cta" data-testid="sp-nav-quote">
              Free assessment
            </Link>
            <button type="button" className="sp-icon-btn sp-burger" aria-label="Menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)} data-testid="sp-burger">
              <Icon name={menuOpen ? 'X' : 'Menu'} size={20} />
            </button>
          </div>
        </div>
        <div className={`sp-mobile${menuOpen ? ' open' : ''}`}>
          {PAGES.map((p) => (
            <Link key={p.path || 'home'} to={href(p.path)}>{p.label}</Link>
          ))}
          <a href={telHref(config.phone_number)}>Call {config.phone_display}</a>
        </div>
      </header>

      <main>
        {page === '' && <HomePage {...ctx} />}
        {page === 'services' && <ServicesPage {...ctx} />}
        {page === 'industries' && <IndustriesPage {...ctx} />}
        {page === 'about' && <AboutPage {...ctx} />}
        {page === 'careers' && <CareersPage {...ctx} />}
        {page === 'contact' && <ContactPage {...ctx} />}
        {page === '__404__' && <NotFoundPage {...ctx} />}
      </main>

      <Footer {...ctx} />
      <div className="sp-sticky">
        <a className="sp-btn sp-btn-outline sp-btn-sm" href={telHref(config.phone_number)}><Icon name="Phone" size={16} /> Call</a>
        <Link className="sp-btn sp-btn-primary sp-btn-sm" to={href('contact')}>Free assessment</Link>
      </div>
    </div>
  );
}

/* ── Head / SEO ───────────────────────────────────────────────── */

function SiteHead({ config, title, page, theme }) {
  const seo = config.seo || {};
  const a = config.address || {};
  const canonical = (seo.canonical || '').replace(/\/$/, '') + (page && page !== '__404__' ? `/${page}` : '/');
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: config.business_name,
    slogan: config.tagline,
    url: seo.canonical,
    telephone: config.phone_number,
    email: config.email_public,
    logo: (seo.canonical || '').replace(/\/$/, '') + config.logo.mark,
    image: (seo.canonical || '').replace(/\/$/, '') + config.logo.full_dark,
    address: { '@type': 'PostalAddress', streetAddress: a.street, addressLocality: a.city, addressRegion: a.region, postalCode: a.postal, addressCountry: a.country },
    areaServed: { '@type': 'State', name: 'California' },
    openingHours: 'Mo-Su 00:00-23:59',
    makesOffer: (config.service_groups || []).flatMap((g) => g.services).map((s) => ({ '@type': 'Offer', itemOffered: { '@type': 'Service', name: s.name } })),
  };
  return (
    <Helmet>
      <html lang="en" />
      <title>{title}</title>
      {seo.description && <meta name="description" content={seo.description} />}
      {seo.keywords && <meta name="keywords" content={seo.keywords.join(', ')} />}
      {page === '__404__' ? <meta name="robots" content="noindex" /> : <link rel="canonical" href={canonical} />}
      <meta name="theme-color" content={theme === 'dark' ? '#060d1a' : '#0b1f3a'} />
      <link rel="icon" href={config.logo.favicon_ico} />
      <link rel="icon" type="image/png" sizes="32x32" href={config.logo.favicon_32} />
      <link rel="apple-touch-icon" href={config.logo.favicon_192} />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Sora:wght@600;700;800&display=swap" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      {seo.description && <meta property="og:description" content={seo.description} />}
      <meta property="og:image" content={(seo.canonical || '').replace(/\/$/, '') + config.logo.full_dark} />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      {page === '' && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: (config.faqs || []).map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
          })}
        </script>
      )}
    </Helmet>
  );
}

/* ── Chrome ───────────────────────────────────────────────────── */

function UtilityBar({ config }) {
  const a = config.address || {};
  return (
    <div className="sp-util">
      <div className="sp-wrap">
        <div className="sp-util-left">
          <span className="sp-util-item"><span className="sp-live" aria-hidden="true" /> Answered 24/7</span>
          <span className="sp-util-item sp-util-hide"><Icon name="MapPin" size={14} /> {a.city}, {a.region} · Serving all of California</span>
        </div>
        <a href={telHref(config.phone_number)} className="sp-util-item" data-testid="sp-util-phone"><Icon name="Phone" size={14} /> {config.phone_display}</a>
      </div>
    </div>
  );
}

function Footer({ config, href }) {
  const a = config.address || {};
  const lead = (config.leadership || [])[0];
  const year = new Date().getFullYear();
  return (
    <footer className="sp-footer" data-testid="sp-footer">
      <div className="sp-wrap">
        <div className="sp-footer-grid">
          <div>
            <img src={config.logo.dark} alt={config.business_name} />
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              Licensed and insured security officers for businesses and private clients across California.
            </p>
          </div>
          <div>
            <h4>Company</h4>
            {PAGES.slice(1).map((p) => <Link key={p.path} to={href(p.path)}>{p.label}</Link>)}
          </div>
          <div>
            <h4>Services</h4>
            {(config.service_groups || []).map((g) => (
              <Link key={g.id} to={`${href('services')}#${g.id}`}>{g.title}</Link>
            ))}
            <Link to={href('contact')}>Free assessment</Link>
          </div>
          <div>
            <h4>Contact</h4>
            <a href={telHref(config.phone_number)}>Office: {config.phone_display}</a>
            {lead && <a href={telHref(lead.phone)}>{lead.name}: {lead.phone_display}</a>}
            <a href={`mailto:${config.email_public}`}>{config.email_public}</a>
            <span style={{ display: 'block', padding: '4px 0' }}>{a.street}, {a.city}, {a.region} {a.postal}</span>
          </div>
        </div>
        <div className="sp-footer-bottom">
          <span>© {year} {config.business_name}. All rights reserved.</span>
          <span>{config.license_number ? `CA PPO License #${config.license_number} · ` : ''}Serving all of California</span>
        </div>
      </div>
    </footer>
  );
}

/* ── Shared blocks ────────────────────────────────────────────── */

function PageHero({ kicker, title, text }) {
  return (
    <section className="sp-hero sp-page-hero">
      <div className="sp-wrap">
        <div>
          <span className="sp-eyebrow">{kicker}</span>
          <h1>{title}</h1>
          {text && <p className="sp-lead">{text}</p>}
        </div>
      </div>
    </section>
  );
}

function CtaBand({ config, href }) {
  return (
    <section className="sp-section" style={{ paddingTop: 0 }}>
      <div className="sp-wrap">
        <div className="sp-band" data-testid="sp-cta-band">
          <div>
            <h2>Get a free security assessment</h2>
            <p>Tell us about your site. A manager will call you back with a plan and a quote.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to={href('contact')} className="sp-btn sp-btn-white">Request a quote <Icon name="ArrowRight" size={16} /></Link>
            <a href={telHref(config.phone_number)} className="sp-btn sp-btn-ghost"><Icon name="Phone" size={16} /> {config.phone_display}</a>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ s }) {
  return (
    <div className="sp-card" data-testid={`sp-service-${s.id}`}>
      <span className="sp-ic"><Icon name={s.icon} /></span>
      <h3>{s.name}</h3>
      <p>{s.text}</p>
    </div>
  );
}

function HeroVisual({ config }) {
  const photo = (config.photos || {}).hero;
  return (
    <div className={`sp-hero-visual${photo ? ' sp-hero-visual-photo' : ''}`} data-testid="sp-hero-visual">
      {photo ? (
        <img className="sp-hero-photo" data-testid="sp-hero-photo" src={photo.src} srcSet={photo.srcSet}
          sizes="(max-width: 980px) 92vw, 560px" alt={photo.alt || ''} fetchpriority="high" />
      ) : (
        <>
          <div className="sp-orbit" aria-hidden="true" />
          <div className="sp-orbit sp-orbit-2" aria-hidden="true" />
          <img className="sp-hero-mark" src={config.logo.mark} alt="" />
        </>
      )}
      <div className="sp-float sp-float-1" aria-hidden="true">
        <span className="sp-float-ic"><Icon name="ShieldCheck" size={18} /></span>
        <div><b>Officer on post</b><small>Site check complete · 2:14 AM</small></div>
      </div>
      <div className="sp-float sp-float-2" aria-hidden="true">
        <span className="sp-float-ic"><Icon name="Car" size={18} /></span>
        <div><b>Patrol route</b><small>6 of 6 stops cleared</small></div>
      </div>
      <div className="sp-float sp-float-3" aria-hidden="true">
        <span className="sp-float-ic"><Icon name="FileText" size={18} /></span>
        <div><b>Daily report</b><small>Sent to your inbox</small></div>
      </div>
    </div>
  );
}

function Faq({ faqs }) {
  return (
    <div className="sp-faq">
      {faqs.map((f) => (
        <details key={f.q}>
          <summary>{f.q}</summary>
          <p>{f.a}</p>
        </details>
      ))}
    </div>
  );
}

/* ── Pages ────────────────────────────────────────────────────── */

function HomePage({ config, href }) {
  const h = config.hero || {};
  const featured = (config.service_groups || []).flatMap((g) => g.services).slice(0, 8);
  return (
    <>
      <section className="sp-hero" data-testid="sp-hero">
        <div className="sp-wrap">
          <div>
            <span className="sp-eyebrow"><Icon name="BadgeCheck" size={16} /> {h.eyebrow}</span>
            <h1>{h.headline}</h1>
            <p className="sp-lead">{h.subheadline}</p>
            <div className="sp-hero-ctas">
              <Link to={href('contact')} className="sp-btn sp-btn-primary" data-testid="sp-hero-quote">{h.primary_cta} <Icon name="ArrowRight" size={16} /></Link>
              <a href={telHref(config.phone_number)} className="sp-btn sp-btn-ghost" data-testid="sp-hero-call"><Icon name="Phone" size={16} /> {h.secondary_cta}</a>
            </div>
          </div>
          <HeroVisual config={config} />
        </div>
      </section>

      <div className="sp-stats">
        <div className="sp-wrap">
          <div className="sp-stats-grid">
            {(config.stats || []).map((s) => (
              <div className="sp-stat" key={s.label}><b>{s.value}</b><span>{s.label}</span></div>
            ))}
          </div>
        </div>
      </div>

      <section className="sp-section">
        <div className="sp-wrap">
          <div className="sp-head-row">
            <div>
              <span className="sp-kicker">What we do</span>
              <h2 className="sp-h2">Security services for every kind of site</h2>
              <p className="sp-sub">Commercial or private, one officer or a full team. We build the coverage around your site, your hours and your risks.</p>
            </div>
            <Link to={href('services')} className="sp-btn sp-btn-outline">All services <Icon name="ArrowRight" size={16} /></Link>
          </div>
          <div className="sp-grid sp-grid-4">
            {featured.map((s) => <ServiceCard key={s.id} s={s} />)}
          </div>
        </div>
      </section>

      <section className="sp-section sp-section-alt">
        <div className="sp-wrap">
          <div className="sp-split">
            <div>
              <span className="sp-kicker">Why TopFlight</span>
              <h2 className="sp-h2">Protection you can see. People you can reach.</h2>
              <p className="sp-sub">We hire carefully, train for your site and stay in touch. When you call, someone answers, day or night.</p>
              <div className="sp-grid sp-grid-2" style={{ marginTop: 28 }}>
                {(config.why || []).map((w) => (
                  <div key={w.title} className="sp-why">
                    <span className="sp-ic"><Icon name={w.icon} /></span>
                    <div><h3>{w.title}</h3><p>{w.text}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <PhotoPanel config={config} slot="why" />
          </div>
        </div>
      </section>

      <section className="sp-section">
        <div className="sp-wrap">
          <div className="sp-head">
            <span className="sp-kicker">Who we protect</span>
            <h2 className="sp-h2">Industries we serve</h2>
          </div>
          <div className="sp-chip-grid">
            {(config.industries || []).map((i) => (
              <div key={i.name} className="sp-chip"><Icon name={i.icon} size={20} /> {i.name}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="sp-section sp-section-alt">
        <div className="sp-wrap">
          <div className="sp-head">
            <span className="sp-kicker">How it works</span>
            <h2 className="sp-h2">From first call to officers on site</h2>
          </div>
          <div className="sp-steps">
            {(config.steps || []).map((s) => (
              <div key={s.title} className="sp-step"><h3>{s.title}</h3><p>{s.text}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="sp-section">
        <div className="sp-wrap sp-split">
          <div>
            <span className="sp-kicker">Questions</span>
            <h2 className="sp-h2">Common questions</h2>
            <p className="sp-sub">Can't find your answer? Call {config.phone_display}. We answer 24/7 and can book your free assessment on the call.</p>
          </div>
          <Faq faqs={config.faqs || []} />
        </div>
      </section>

      <CtaBand config={config} href={href} />
    </>
  );
}

function PhotoPanel({ config, slot, wide = false }) {
  const photo = (config.photos || {})[slot];
  if (wide && !photo) return null;
  if (photo) {
    return (
      <img className={`sp-photo${wide ? ' sp-photo-wide' : ''}`} data-testid={`sp-photo-${slot}`} src={photo.src} srcSet={photo.srcSet}
        sizes="(max-width: 980px) 92vw, 520px" alt={photo.alt || ''} loading="lazy"
        style={photo.position ? { objectPosition: photo.position } : undefined} />
    );
  }
  return (
    <div className="sp-photo sp-photo-designed" aria-hidden="true">
      <img src={config.logo.full_dark} alt="" />
    </div>
  );
}

function ServicesPage({ config, href }) {
  return (
    <>
      <PageHero kicker="Services" title="Security services, commercial and private" text="Pick one service or combine several. Every plan starts with a free assessment of your site." />
      <section className="sp-section">
        <div className="sp-wrap">
          {(config.service_groups || []).map((g) => (
            <div key={g.id} id={g.id} className="sp-group" data-testid={`sp-group-${g.id}`}>
              <h2 className="sp-group-title">{g.title}</h2>
              <p className="sp-group-intro">{g.intro}</p>
              <div className="sp-grid sp-grid-4">
                {g.services.map((s) => <ServiceCard key={s.id} s={s} />)}
              </div>
            </div>
          ))}
          <div className="sp-card sp-else" style={{ marginTop: 40 }}>
            <h3 style={{ marginTop: 0 }}>Need something else?</h3>
            <p>If it involves keeping people or property safe, ask us. We put together custom coverage for unusual sites and one-time needs.</p>
            <Link to={href('contact')} className="sp-btn sp-btn-primary sp-btn-sm" style={{ marginTop: 14 }}>Tell us what you need</Link>
          </div>
        </div>
      </section>
      <CtaBand config={config} href={href} />
    </>
  );
}

function IndustriesPage({ config, href }) {
  return (
    <>
      <PageHero kicker="Industries" title="Security that fits your industry" text="Each industry has its own risks. Our officers are briefed and trained for the site they protect." />
      <section className="sp-section">
        <div className="sp-wrap">
          <PhotoPanel config={config} slot="industries" wide />
          <div className="sp-grid sp-grid-3">
            {(config.industries || []).map((i) => (
              <div key={i.name} className="sp-card">
                <span className="sp-ic"><Icon name={i.icon} /></span>
                <h3>{i.name}</h3>
                <p>{industryBlurb(i.name)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaBand config={config} href={href} />
    </>
  );
}

function industryBlurb(name) {
  const map = {
    'Commercial offices': 'Lobby officers, access control and after-hours patrols for office buildings and business parks.',
    Construction: 'Night and weekend watch that protects equipment, tools and materials from theft.',
    'Retail and shopping centers': 'Loss prevention and a calm, visible presence for stores and centers.',
    'Warehouses and logistics': 'Gate control, trailer checks and patrols for distribution and storage sites.',
    'HOAs and residential': 'Community patrols, gatehouse officers and rule enforcement for neighborhoods.',
    Healthcare: 'Officers trained to de-escalate and protect patients, staff and visitors.',
    'Schools and campuses': 'Campus patrols, event coverage and safe drop-off and pick-up.',
    'Events and venues': 'Entry screening, crowd management and VIP areas for events of any size.',
    'Manufacturing and industrial': 'Perimeter security, visitor logs and safety checks for plants.',
    'Hotels and hospitality': 'Guest-friendly officers for lobbies, parking and late-night hours.',
    'Government and public sites': 'Professional officers for public buildings and facilities.',
    'Cannabis and agriculture': 'Armed and unarmed coverage for farms, grows and dispensaries.',
  };
  return map[name] || 'Coverage designed around your site and your hours.';
}

function AboutPage({ config, href }) {
  const lead = (config.leadership || [])[0];
  return (
    <>
      <PageHero kicker="About us" title="A California security company that answers the phone" text={`${config.business_name} protects businesses, communities and private clients across California from our office in ${config.address.city}.`} />
      <section className="sp-section">
        <div className="sp-wrap sp-split">
          <div>
            <span className="sp-kicker">Our promise</span>
            <h2 className="sp-h2">Reliable officers. Clear communication. No surprises.</h2>
            <p className="sp-sub" style={{ marginBottom: 16 }}>
              We started {config.business_name} to give clients the kind of security company we would want to hire: one that shows up on time, reports what happened, and picks up when you call.
            </p>
            <p className="sp-sub">
              Our officers are licensed, background-checked and trained for the sites they protect. Every client has a direct line to a supervisor.
            </p>
          </div>
          <PhotoPanel config={config} slot="about" />
        </div>
      </section>
      {lead && (
        <section className="sp-section sp-section-alt">
          <div className="sp-wrap">
            <div className="sp-head"><span className="sp-kicker">Leadership</span><h2 className="sp-h2">Meet our team</h2></div>
            <PhotoPanel config={config} slot="team" wide />
            <div className="sp-person" style={{ maxWidth: 560 }} data-testid="sp-leader">
              <span className="sp-avatar">{lead.name.split(' ').map((x) => x[0]).join('')}</span>
              <div>
                <b style={{ fontSize: 18 }}>{lead.name}</b>
                <div className="sp-fine" style={{ marginBottom: 6 }}>{lead.title}</div>
                <a href={telHref(lead.phone)} style={{ color: 'var(--sp-accent)', fontWeight: 700, textDecoration: 'none', marginRight: 14 }}>{lead.phone_display}</a>
                <a href={`mailto:${lead.email}`} style={{ color: 'var(--sp-accent)', fontWeight: 700, textDecoration: 'none' }}>{lead.email}</a>
              </div>
            </div>
          </div>
        </section>
      )}
      <section className="sp-section">
        <div className="sp-wrap">
          <div className="sp-grid sp-grid-4">
            {(config.why || []).map((w) => (
              <div key={w.title} className="sp-card"><span className="sp-ic"><Icon name={w.icon} /></span><h3>{w.title}</h3><p>{w.text}</p></div>
            ))}
          </div>
        </div>
      </section>
      <CtaBand config={config} href={href} />
    </>
  );
}

function CareersPage({ config }) {
  const c = config.careers || {};
  return (
    <>
      <PageHero kicker="Careers" title={c.headline} text={c.text} />
      <section className="sp-section">
        <div className="sp-wrap sp-contact">
          <div>
            <span className="sp-kicker">What you need</span>
            <h2 className="sp-h2">Requirements</h2>
            <ul className="sp-checks">
              {(c.requirements || []).map((r) => (
                <li key={r}><Icon name="CheckCircle2" size={18} /> {r}</li>
              ))}
            </ul>
            <p className="sp-sub" style={{ marginTop: 18 }}>Questions about a job? Call {config.phone_display}.</p>
            <div style={{ marginTop: 24 }}><PhotoPanel config={config} slot="careers" /></div>
          </div>
          <div className="sp-card">
            <LeadForm config={config} kind="careers" />
          </div>
        </div>
      </section>
    </>
  );
}

function ContactPage({ config }) {
  const a = config.address || {};
  const lead = (config.leadership || [])[0];
  const mapQ = encodeURIComponent(`${a.street}, ${a.city}, ${a.region} ${a.postal}`);
  return (
    <>
      <PageHero kicker="Contact" title="Request a free security assessment" text="Tell us about your site and what you need. A manager will contact you with a plan and a quote." />
      <section className="sp-section">
        <div className="sp-wrap sp-contact">
          <div className="sp-contact-list" data-testid="sp-contact-info">
            <div className="sp-contact-item"><span className="sp-ic"><Icon name="Phone" /></span><div><b>Office (24/7)</b><a href={telHref(config.phone_number)}>{config.phone_display}</a></div></div>
            {lead && (
              <div className="sp-contact-item"><span className="sp-ic"><Icon name="UserCheck" /></span><div><b>{lead.name}, {lead.title}</b><a href={telHref(lead.phone)}>{lead.phone_display}</a><br /><a href={`mailto:${lead.email}`}>{lead.email}</a></div></div>
            )}
            <div className="sp-contact-item"><span className="sp-ic"><Icon name="MapPin" /></span><div><b>Office</b>{a.street}<br />{a.city}, {a.region} {a.postal}</div></div>
            <div className="sp-contact-item"><span className="sp-ic"><Icon name="Clock" /></span><div><b>Hours</b>{config.hours}</div></div>
            <div className="sp-contact-item"><span className="sp-ic"><Icon name="Map" /></span><div><b>Service area</b>{config.service_area}</div></div>
            <iframe className="sp-map" title={`Map to ${config.business_name}`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" src={`https://www.google.com/maps?q=${mapQ}&output=embed`} />
          </div>
          <div className="sp-card" id="quote">
            <LeadForm config={config} kind="quote" />
          </div>
        </div>
      </section>
    </>
  );
}

function NotFoundPage({ href }) {
  return (
    <PageHero kicker="404" title="We couldn't find that page" text={<span>Try the <Link to={href('')} style={{ color: '#9cc3ff' }}>home page</Link> or call us any time.</span>} />
  );
}

/* ── Lead form (quote + careers) ──────────────────────────────── */

function LeadForm({ config, kind }) {
  const careers = kind === 'careers';
  const services = config.quote_form?.services || [];
  const [s, setS] = useState({
    customer_name: '', phone: '', email: '', organization: '', service_address: '',
    project_type: careers ? 'Job application' : services[0] || '', preferred_time: '',
    project_description: '', guard_card: '', armed_permit: 'No', company: '',
  });
  const [status, setStatus] = useState('idle');
  const [err, setErr] = useState('');
  const up = (k) => (e) => setS((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!s.customer_name.trim() || (!s.phone.trim() && !s.email.trim())) {
      setErr('Please enter your name and a phone number or email.');
      setStatus('error');
      return;
    }
    setStatus('sending');
    setErr('');
    const description = careers
      ? [`Job application.`, s.guard_card && `Guard card #: ${s.guard_card}.`, `Firearm permit: ${s.armed_permit}.`, s.project_description].filter(Boolean).join(' ')
      : [s.organization && `Company: ${s.organization}.`, s.project_description].filter(Boolean).join(' ');
    try {
      const r = await fetch(`${API}/api/website-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: config.slug,
          industry: config.industry,
          source_domain: typeof window !== 'undefined' ? window.location.hostname : '',
          customer_name: s.customer_name,
          phone: s.phone,
          email: s.email,
          service_address: s.service_address,
          project_type: s.project_type,
          project_description: description,
          preferred_time: s.preferred_time,
          company: s.company, // honeypot
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(typeof d.detail === 'string' ? d.detail : `Could not send (${r.status})`);
      }
      setStatus('done');
    } catch (ex) {
      setErr(`${ex.message || 'Could not send.'} Please call ${config.phone_display}.`);
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className="sp-ok" data-testid="sp-form-success" role="status">
        <Icon name="CheckCircle2" size={44} />
        <h3 style={{ fontSize: 22 }}>{careers ? 'Application received' : 'Request received'}</h3>
        <p className="sp-sub" style={{ margin: '0 auto' }}>
          {careers ? 'Thank you. Our hiring team will contact you soon.' : config.quote_form?.success_message}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid={`sp-form-${kind}`}>
      <h2 style={{ fontSize: 22, margin: '0 0 6px' }}>{careers ? 'Apply now' : 'Get your free quote'}</h2>
      <p className="sp-fine" style={{ margin: '0 0 18px' }}>{careers ? 'It takes about a minute.' : 'We reply quickly, usually the same day.'}</p>
      <div className="sp-form">
        <div className="sp-field"><label htmlFor={`${kind}-name`}>Full name *</label><input id={`${kind}-name`} autoComplete="name" value={s.customer_name} onChange={up('customer_name')} data-testid="sp-in-name" /></div>
        <div className="sp-field"><label htmlFor={`${kind}-phone`}>Phone *</label><input id={`${kind}-phone`} type="tel" autoComplete="tel" value={s.phone} onChange={up('phone')} data-testid="sp-in-phone" /></div>
        <div className="sp-field"><label htmlFor={`${kind}-email`}>Email</label><input id={`${kind}-email`} type="email" autoComplete="email" value={s.email} onChange={up('email')} data-testid="sp-in-email" /></div>
        {careers ? (
          <>
            <div className="sp-field"><label htmlFor="c-card">Guard card number</label><input id="c-card" value={s.guard_card} onChange={up('guard_card')} /></div>
            <div className="sp-field"><label htmlFor="c-armed">Firearm permit?</label><select id="c-armed" value={s.armed_permit} onChange={up('armed_permit')}><option>No</option><option>Yes</option></select></div>
            <div className="sp-field"><label htmlFor="c-city">City you live in</label><input id="c-city" value={s.service_address} onChange={up('service_address')} /></div>
          </>
        ) : (
          <>
            <div className="sp-field"><label htmlFor="q-org">Company (if any)</label><input id="q-org" autoComplete="organization" value={s.organization} onChange={up('organization')} /></div>
            <div className="sp-field sp-field-full"><label htmlFor="q-site">Site address (California)</label><input id="q-site" autoComplete="street-address" value={s.service_address} onChange={up('service_address')} data-testid="sp-in-site" /></div>
            <div className="sp-field"><label htmlFor="q-service">Service needed</label><select id="q-service" value={s.project_type} onChange={up('project_type')} data-testid="sp-in-service">{services.map((x) => <option key={x}>{x}</option>)}</select></div>
            <div className="sp-field"><label htmlFor="q-when">When do you need it?</label><input id="q-when" placeholder="e.g. next Monday, nights" value={s.preferred_time} onChange={up('preferred_time')} /></div>
          </>
        )}
        <div className="sp-field sp-field-full"><label htmlFor={`${kind}-msg`}>{careers ? 'Experience (optional)' : 'Tell us about your site'}</label><textarea id={`${kind}-msg`} value={s.project_description} onChange={up('project_description')} data-testid="sp-in-msg" /></div>
        <div className="sp-hp" aria-hidden="true"><label>Company<input tabIndex={-1} autoComplete="off" value={s.company} onChange={up('company')} /></label></div>
        {status === 'error' && <div className="sp-err sp-field-full" role="alert" data-testid="sp-form-error">{err}</div>}
        <div className="sp-field-full">
          <button type="submit" className="sp-btn sp-btn-primary" style={{ width: '100%' }} disabled={status === 'sending'} data-testid="sp-submit">
            {status === 'sending' ? <><Icon name="Loader2" size={16} className="sp-spin" /> Sending…</> : <>{careers ? 'Send application' : 'Request my free quote'} <Icon name="ArrowRight" size={16} /></>}
          </button>
          <p className="sp-fine" style={{ marginTop: 10 }}>By sending, you agree that {config.business_name} may contact you by phone, text or email about your request.</p>
        </div>
      </div>
    </form>
  );
}
